import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2 } from "lucide-react";
import wealthyStewardLogo from "@/assets/wealthy-steward-logo.png";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { fetchUserRoles, pickPreferredHome } from "@/hooks/useUserRoles";
import { sendFirstTimeAccessEmail } from "@/lib/authEmails";
import { isOnboardingComplete } from "@/lib/onboardingComplete";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const explicitRedirect = searchParams.get("redirect");

  const resolveDestination = async (userId: string) => {
    if (explicitRedirect) return explicitRedirect;
    const roles = await fetchUserRoles(userId);
    const roleHome = pickPreferredHome(roles);
    if (roleHome) return roleHome;

    // Members still forming their Association have their own home.
    const { data: record } = await (supabase as any)
      .from("member_records")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    const stage = record?.fulfillment_stage as string | undefined;
    // A member whose documents are delivered, or who came over in the legacy
    // import, already has an Association. They land in the portal, never in the
    // new client onboarding.
    if (stage === "docs_delivered" || record?.origin === "legacy_import") return "/journey";
    if (record && !isOnboardingComplete(record)) return "/onboarding";
    if (stage && ["pending", "ready", "ein_acquired", "filings_submitted", "filed_with_state", "filings_received", "state_processed", "docs_sent"].includes(stage)) {
      return "/tracking";
    }

    return "/journey";
  };

  useEffect(() => {
    // Check if user is already logged in
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        navigate(await resolveDestination(session.user.id));
      }
    };
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, explicitRedirect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast({
          title: "Login failed",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      if (data.session) {
        toast({
          title: "Welcome back!",
          description: "You've successfully signed in.",
        });
        navigate(await resolveDestination(data.session.user.id));
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendAccessLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setResendLoading(true);
    try {
      // Check if user exists in provisioning log
      const { data: provLog } = await supabase
        .from('user_provisioning_log')
        .select('*')
        .eq('email', resendEmail.toLowerCase())
        .single();

      if (!provLog) {
        toast({
          title: "Account not found",
          description: "We couldn't find an account with that email. Please contact support if you've made a purchase.",
          variant: "destructive",
        });
        return;
      }

      // Send the access link through our own templated pipeline
      const result = await sendFirstTimeAccessEmail(resendEmail);

      if (!result.success) throw new Error(result.message);

      toast({
        title: "Access link sent!",
        description: "Check your email for your password setup link.",
      });
      setResendEmail("");
    } catch (error: any) {
      toast({
        title: "Failed to send link",
        description: error.message || "Please try again or contact support",
        variant: "destructive",
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Logo and Slogan */}
      <div className="hidden lg:flex lg:w-2/5 auth-gold-panel flex-col items-center justify-center">
        <img 
          src={wealthyStewardLogo} 
          alt="Wealthy Steward" 
          className="h-32 md:h-40 w-auto mb-8"
        />
        <p className="text-foreground text-xl md:text-2xl font-light tracking-wide text-center">
          Own Nothing, Steward Everything
        </p>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center lg:hidden mb-8">
            <img 
              src={wealthyStewardLogo} 
              alt="Wealthy Steward" 
              className="h-20 w-auto mx-auto mb-2"
            />
            <p className="text-sm text-muted-foreground">Own Nothing, Steward Everything</p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-foreground mb-2">Welcome back</h2>
            <p className="text-muted-foreground">
              Enter your credentials to access your account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
              />
              <label htmlFor="remember" className="text-sm text-muted-foreground">
                Remember me for 30 days
              </label>
            </div>

            <Button type="submit" className="w-full h-11 text-base" disabled={loading}>
              {loading ? "Signing in..." : "Enter"}
            </Button>
          </form>

          {/* First time here section */}
          <div className="pt-4 border-t border-border">
            <div className="text-center mb-3">
              <h3 className="text-sm font-semibold text-foreground">First time here?</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Purchased but haven't received your access email?
              </p>
            </div>
            <form onSubmit={handleResendAccessLink} className="flex gap-2">
              <Input
                type="email"
                placeholder="your@email.com"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="h-10"
              />
              <Button 
                type="submit" 
                variant="outline" 
                size="sm" 
                disabled={resendLoading}
                className="h-10 px-4 whitespace-nowrap"
              >
                {resendLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-1" />
                    Resend
                  </>
                )}
              </Button>
            </form>
          </div>

          <div className="pt-4 border-t border-border">
            <p className="text-xs text-center text-muted-foreground">
              By signing in, you agree to our{" "}
              <Link to="/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;